import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CONFIDENCE_LABELS,
  api,
  formatDate,
  mineralTitle,
  type Confidence,
  type Me,
  type Trip,
} from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { SuggestField } from '../components/SuggestField.tsx';
import { PhotoPicker, type PendingPhoto } from '../components/PhotoPicker.tsx';
import { IdentifyDraft } from '../components/IdentifyDraft.tsx';
import { CoordPicker } from '../components/CoordPicker.tsx';

type MineralRow = { name: string; confidence?: Confidence; note?: string };

type Draft = {
  minerals: MineralRow[];
  trade_name: string;
  acquisition_type: 'found' | 'purchased' | 'unknown';
  acquired_on: string;
  acquired_from: string;
  acquired_place: string;
  trip_id: string | null;
  locality_name: string;
  locality_country: string;
  /** Выбранная из подсказки запись справочника, если её выбирали. */
  locality_id: number | null;
  locality_note: string;
  size_x_mm: string;
  size_y_mm: string;
  size_z_mm: string;
  weight_g: string;
  find_latitude: string;
  find_longitude: string;
  find_precision: string;
  purchase_amount: string;
  purchase_currency: string;
  storage_location: string;
  notes: string;
};

const EMPTY: Draft = {
  minerals: [{ name: '' }],
  trade_name: '',
  acquisition_type: 'found',
  acquired_on: '',
  acquired_from: '',
  acquired_place: '',
  trip_id: null,
  locality_name: '',
  locality_country: '',
  locality_id: null,
  locality_note: '',
  size_x_mm: '',
  size_y_mm: '',
  size_z_mm: '',
  weight_g: '',
  find_latitude: '',
  find_longitude: '',
  find_precision: '',
  purchase_amount: '',
  purchase_currency: '',
  storage_location: '',
  notes: '',
};

/**
 * Достоверность по умолчанию зависит от того, как образец попал в коллекцию
 * (ТЗ 2.4): у покупки это слова продавца, у находки — ваше собственное
 * определение. Пока поле не тронули руками, оно следует за типом
 * поступления; как только выбрали явно — остаётся выбранным.
 */
function defaultConfidence(type: Draft['acquisition_type']): Confidence {
  return type === 'purchased' ? 'as_labelled' : 'probable';
}

/** Пустая строка означает «поле не заполнено», а не пустое значение. */
const text = (v: string) => (v.trim() ? v.trim() : null);
const num = (v: string) => (v.trim() ? Number(v.replace(',', '.')) : null);

export function SpecimenFormScreen({ me }: { me: Me }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const editing = Boolean(id);

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [showMore, setShowMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(!editing);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  useEffect(() => {
    void api.trips().then(setTrips);
  }, []);

  useEffect(() => {
    if (!id) return;
    api
      .specimen(id)
      .then((s) => {
        setDraft({
          minerals: s.minerals.length
            ? s.minerals.map((m) => ({
                name: m.name,
                confidence: m.confidence,
                note: m.note ?? undefined,
              }))
            : [{ name: '' }],
          trade_name: s.trade_name ?? '',
          acquisition_type:
            s.acquisition_type === 'found' || s.acquisition_type === 'purchased'
              ? s.acquisition_type
              : 'unknown',
          acquired_on: s.acquired_on?.slice(0, 10) ?? '',
          acquired_from: s.acquired_from ?? '',
          acquired_place: s.acquired_place ?? '',
          trip_id: s.trip_id,
          // Показываем собственное место образца, а не унаследованное от
          // выезда: иначе правка формы молча превратит наследование в копию.
          locality_name: s.inherited_from_trip ? '' : s.locality_name ?? '',
          locality_country: s.inherited_from_trip ? '' : s.locality_country ?? '',
          // Связь со справочником переживает правку остальных полей: без
          // этого открытая и сохранённая карточка теряла бы месторождение
          // и заводила вместо него одноимённую строку.
          locality_id: s.inherited_from_trip ? null : s.locality_id,
          locality_note: s.locality_note ?? '',
          size_x_mm: s.size_x_mm ? String(Number(s.size_x_mm)) : '',
          size_y_mm: s.size_y_mm ? String(Number(s.size_y_mm)) : '',
          size_z_mm: s.size_z_mm ? String(Number(s.size_z_mm)) : '',
          weight_g: s.weight_g ? String(Number(s.weight_g)) : '',
          // Собственная точка образца, а не унаследованная от выезда:
          // иначе правка формы молча превратила бы наследование в копию.
          find_latitude: s.find_latitude ? String(Number(s.find_latitude)) : '',
          find_longitude: s.find_longitude ? String(Number(s.find_longitude)) : '',
          find_precision:
            !s.inherited_from_trip && s.effective_precision_m != null
              ? String(s.effective_precision_m)
              : '',
          purchase_amount: s.purchase_amount ? String(Number(s.purchase_amount)) : '',
          purchase_currency: s.purchase_currency ?? '',
          storage_location: s.storage_location ?? '',
          notes: s.notes ?? '',
        });
        setShowMore(true);
        setLoaded(true);
      })
      .catch((e: Error) => {
        setError(e.message);
        setLoaded(true);
      });
  }, [id]);

  const mineralSuggestions = useCallback(
    (q: string) =>
      api.mineralSuggestions(q).then((rows) =>
        rows.map((r) => ({
          key: String(r.id),
          // Показываем русское название, если оно известно: справочник
          // латинский, а коллекцию ведут по-русски (ТЗ, открытый вопрос 2).
          title: mineralTitle(r),
          rank: [
            r.kind === 'variety'
              ? `разновидность · ${mineralTitle({ name: r.parent_name ?? '', name_ru: r.parent_name_ru })}`
              : r.mindat_id
                ? 'вид из справочника'
                : 'вводили раньше',
            r.formula,
            r.name_ru ? r.name : null,
          ]
            .filter(Boolean)
            .join(' · '),
        })),
      ),
    [],
  );

  const localitySuggestions = useCallback(
    (q: string) =>
      api.localitySuggestions(q).then((rows) =>
        rows.map((r) => ({
          key: String(r.id),
          title: r.name,
          rank: r.country ?? undefined,
          country: r.country,
        })),
      ),
    [],
  );

  const countrySuggestions = useCallback(
    (q: string) =>
      api.countrySuggestions(q).then((rows) =>
        rows.map((r) => ({
          key: r.name,
          title: r.name,
          rank: r.mine ? 'уже в коллекции' : undefined,
        })),
      ),
    [],
  );

  const canSave = draft.minerals.some((m) => m.name.trim());

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body = {
        minerals: draft.minerals
          .filter((m) => m.name.trim())
          .map((m, index) => ({
            name: m.name.trim(),
            is_primary: index === 0,
            confidence: m.confidence,
            note: m.note ?? null,
          })),
        trade_name: text(draft.trade_name),
        acquisition_type: draft.acquisition_type,
        acquired_on: text(draft.acquired_on),
        acquired_from: text(draft.acquired_from),
        acquired_place: text(draft.acquired_place),
        trip_id: draft.trip_id,
        locality_name: text(draft.locality_name),
        locality_country: text(draft.locality_country),
        locality_id: draft.locality_id,
        locality_note: text(draft.locality_note),
        size_x_mm: num(draft.size_x_mm),
        size_y_mm: num(draft.size_y_mm),
        size_z_mm: num(draft.size_z_mm),
        weight_g: num(draft.weight_g),
        find_latitude: num(draft.find_latitude),
        find_longitude: num(draft.find_longitude),
        find_coord_source: num(draft.find_latitude) !== null ? ('manual' as const) : null,
        find_coord_precision_m: num(draft.find_precision),
        // В режиме «без цен» сервер не присылает сумму, и форма её не видела.
        // Отправить null здесь значило бы стереть цену, которую владелец
        // просто спрятал (ТЗ D4: режим прячет, а не удаляет).
        ...(me.hide_prices
          ? {}
          : {
              purchase_amount: num(draft.purchase_amount),
              purchase_currency:
                num(draft.purchase_amount) === null
                  ? null
                  : (draft.purchase_currency || me.default_currency).toUpperCase(),
            }),
        storage_location: text(draft.storage_location),
        notes: text(draft.notes),
      };

      const saved = id ? await api.updateSpecimen(id, body) : await api.createSpecimen(body);

      for (const [index, photo] of photos.entries()) {
        setBusy(true);
        setUploading(
          photos.length > 1 ? `Снимок ${index + 1} из ${photos.length}…` : 'Загружаем снимок…',
        );
        const form = new FormData();
        form.append('full', photo.prepared.full, 'full.webp');
        form.append('thumb', photo.prepared.thumb, 'thumb.webp');
        form.append('kind', photo.kind);
        form.append('width', String(photo.prepared.width));
        form.append('height', String(photo.prepared.height));
        if (photo.prepared.takenAt) form.append('taken_at', photo.prepared.takenAt);
        if (photo.prepared.latitude !== null) {
          form.append('exif_latitude', String(photo.prepared.latitude));
        }
        if (photo.prepared.longitude !== null) {
          form.append('exif_longitude', String(photo.prepared.longitude));
        }
        await api.uploadPhoto(saved.id, form);
      }

      navigate(`/specimen/${saved.id}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
      setUploading(null);
      setBusy(false);
    }
  }

  if (!loaded) {
    return (
      <div className="app">
        <div className="scroll muted small">Загрузка…</div>
      </div>
    );
  }

  const selectedTrip = trips.find((t) => t.id === draft.trip_id);

  return (
    <div className="app">
      <div className="topbar topbar--bordered">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Отмена">
          <Icon name="x" className="icon muted" />
        </button>
        <div className="spacer" />
        <h2 className="topbar-title">{editing ? 'Правка образца' : 'Новый образец'}</h2>
        <div className="spacer" />
        <button className="link-btn" disabled={!canSave || busy} onClick={save}>
          {busy ? '…' : 'Сохранить'}
        </button>
      </div>

      <div className="scroll">
        <PhotoPicker photos={photos} onChange={setPhotos} />

        <div className="label-key">Минерал</div>
        {draft.minerals.map((mineral, index) => (
          <div key={index} className="mineral-row">
            <SuggestField
              value={mineral.name}
              autoFocus={index === 0 && !editing}
              placeholder={index === 0 ? 'главный вид' : 'сопутствующий'}
              onChange={(value) =>
                setDraft((d) => {
                  const minerals = [...d.minerals];
                  minerals[index] = { ...minerals[index]!, name: value };
                  return { ...d, minerals };
                })
              }
              fetchSuggestions={mineralSuggestions}
            />
            {/* Спрашивать имеет смысл только про главный минерал и только
                пока имени нет: подсказка поверх заполненного поля — это
                предложение переделать то, что человек уже решил. */}
            {index === 0 && !mineral.name.trim() && (
              <IdentifyDraft
                photos={photos.map((photo) => photo.prepared.full)}
                localityName={draft.locality_name ?? null}
                localityCountry={draft.locality_country ?? null}
                onPick={(name) =>
                  setDraft((d) => {
                    const minerals = [...d.minerals];
                    minerals[0] = { ...minerals[0]!, name, confidence: 'uncertain' };
                    return { ...d, minerals };
                  })
                }
              />
            )}
            <div className="confidence-row">
              <label className="hint" htmlFor={`confidence-${index}`}>
                достоверность
              </label>
              <select
                id={`confidence-${index}`}
                className="field-input field-compact"
                value={mineral.confidence ?? defaultConfidence(draft.acquisition_type)}
                onChange={(e) =>
                  setDraft((d) => {
                    const minerals = [...d.minerals];
                    minerals[index] = {
                      ...minerals[index]!,
                      confidence: e.target.value as Confidence,
                    };
                    return { ...d, minerals };
                  })
                }
              >
                {(Object.keys(CONFIDENCE_LABELS) as Confidence[]).map((value) => (
                  <option key={value} value={value}>
                    {CONFIDENCE_LABELS[value]}
                  </option>
                ))}
              </select>
            </div>

            {index === 0 ? (
              <span className="hint">главный — идёт в заголовок и на этикетку</span>
            ) : (
              <button
                type="button"
                className="link-btn link-btn--quiet"
                onClick={() =>
                  setDraft((d) => ({
                    ...d,
                    minerals: d.minerals.filter((_, i) => i !== index),
                  }))
                }
              >
                убрать
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          className="link-btn link-btn--quiet"
          onClick={() => setDraft((d) => ({ ...d, minerals: [...d.minerals, { name: '' }] }))}
        >
          + ещё минерал
        </button>

        <div className="label-key">Как попал в коллекцию</div>
        <div className="segment">
          {(
            [
              ['found', 'Находка'],
              ['purchased', 'Покупка'],
              ['unknown', 'Другое'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={draft.acquisition_type === value ? 'is-selected' : ''}
              onClick={() => set('acquisition_type', value)}
            >
              {label}
            </button>
          ))}
        </div>

        {draft.acquisition_type === 'purchased' && (
          <>
            <div className="label-key">Продавец</div>
            <input
              className="field-input"
              value={draft.acquired_from}
              onChange={(e) => set('acquired_from', e.target.value)}
            />
            <div className="label-key">Площадка или выставка</div>
            <input
              className="field-input"
              value={draft.acquired_place}
              onChange={(e) => set('acquired_place', e.target.value)}
            />
            <div className="label-key">Что написано на этикетке продавца</div>
            <input
              className="field-input"
              value={draft.trade_name}
              placeholder="например, вишнёвый кварц"
              onChange={(e) => set('trade_name', e.target.value)}
            />
            <div className="hint">Хранится отдельно от систематики — это цитата, а не определение</div>

            {!me.hide_prices && (
              <>
                <div className="label-key">Цена покупки</div>
                <div className="size-row" style={{ gridTemplateColumns: '2fr 1fr' }}>
                  <input
                    className="field-input"
                    inputMode="decimal"
                    placeholder="сумма"
                    value={draft.purchase_amount}
                    onChange={(e) => set('purchase_amount', e.target.value)}
                  />
                  <input
                    className="field-input"
                    placeholder={me.default_currency}
                    maxLength={3}
                    value={draft.purchase_currency}
                    onChange={(e) =>
                      set('purchase_currency', e.target.value.replace(/[^a-zA-Zа-яА-Я]/g, ''))
                    }
                  />
                </div>
                <div className="hint">
                  Записывается один раз. Как меняется стоимость дальше — журнал оценок в карточке
                </div>
              </>
            )}
          </>
        )}

        <div className="label-key">Дата поступления</div>
        <input
          className="field-input"
          type="date"
          value={draft.acquired_on}
          onChange={(e) => set('acquired_on', e.target.value)}
        />

        {draft.acquisition_type === 'found' && (
          <>
            <div className="label-key">Выезд</div>
            {selectedTrip ? (
              <div className="trip-picked">
                <Icon name="route" className="icon" />
                <div>
                  {selectedTrip.title ?? selectedTrip.locality_name ?? 'выезд'}
                  <span className="hint">место и координаты возьмутся отсюда</span>
                </div>
                <div className="spacer" />
                <button type="button" className="link-btn link-btn--quiet" onClick={() => set('trip_id', null)}>
                  отвязать
                </button>
              </div>
            ) : (
              <div className="hint">
                Привяжите выезд — и место не придётся вводить для каждого образца
              </div>
            )}
            <div className="chips">
              {trips
                .filter((t) => t.id !== draft.trip_id)
                .slice(0, 6)
                .map((trip) => (
                  <button
                    key={trip.id}
                    type="button"
                    className="chip chip--plain"
                    onClick={() => set('trip_id', trip.id)}
                  >
                    {trip.title ?? trip.locality_name ?? formatDate(trip.started_on)}
                    {trip.specimen_count > 0 ? ` · ${trip.specimen_count}` : ''}
                  </button>
                ))}
              <button
                type="button"
                className="chip chip--plain"
                onClick={() => navigate('/trips/new')}
              >
                + новый выезд
              </button>
            </div>
          </>
        )}

        {!showMore ? (
          <>
            <button
              type="button"
              className="btn btn--full"
              style={{ marginTop: 'var(--space-5)' }}
              onClick={() => setShowMore(true)}
            >
              Остальные поля
            </button>
            <div className="hint centered">Можно дозаполнить позже, из карточки</div>
          </>
        ) : (
          <>
            <div className="label-key">
              Месторождение
              {draft.trip_id && !draft.locality_name && (
                <span className="hint"> — берётся из выезда, заполните только чтобы переопределить</span>
              )}
            </div>
            <SuggestField
              value={draft.locality_name}
              onChange={(v) => {
                // Название правят руками — значит выбранная запись
                // справочника больше не та. Держаться за неё нельзя:
                // иначе «Додо» молча останется ссылкой на Дальнегорск.
                setDraft((d) => ({ ...d, locality_name: v, locality_id: null }));
              }}
              onPick={(item) => {
                setDraft((d) => ({
                  ...d,
                  locality_name: item.title,
                  locality_id: Number(item.key),
                  locality_country: item.country ?? d.locality_country,
                }));
              }}
              fetchSuggestions={localitySuggestions}
              placeholder="Додо"
            />
            <div style={{ marginTop: 'var(--space-2)' }}>
              <SuggestField
                value={draft.locality_country}
                onChange={(v) => set('locality_country', v)}
                fetchSuggestions={countrySuggestions}
                placeholder="страна"
              />
            </div>
            <input
              className="field-input"
              style={{ marginTop: 'var(--space-2)' }}
              value={draft.locality_note}
              placeholder="уточнение: горизонт, жила, отвал"
              onChange={(e) => set('locality_note', e.target.value)}
            />

            <div className="label-key">Размер, мм</div>
            <div className="size-row">
              {(['size_x_mm', 'size_y_mm', 'size_z_mm'] as const).map((key, i) => (
                <input
                  key={key}
                  className="field-input"
                  inputMode="decimal"
                  placeholder={['длина', 'ширина', 'высота'][i]}
                  value={draft[key]}
                  onChange={(e) => set(key, e.target.value)}
                />
              ))}
            </div>

            <div className="label-key">Вес, г</div>
            <input
              className="field-input"
              inputMode="decimal"
              value={draft.weight_g}
              onChange={(e) => set('weight_g', e.target.value)}
            />

            <CoordPicker
              label="Координаты находки"
              latitude={draft.find_latitude}
              longitude={draft.find_longitude}
              precisionM={draft.find_precision}
              onChange={(next) =>
                setDraft((d) => ({
                  ...d,
                  find_latitude: next.latitude,
                  find_longitude: next.longitude,
                  find_precision: next.precisionM,
                }))
              }
            />
            <div className="hint">
              {draft.trip_id
                ? 'Своя точка переопределит координаты выезда — нужна, если в одну поездку обошли два обнажения'
                : 'Обычно координаты ставят на выезде, один раз для всей партии'}
            </div>

            <div className="label-key">Место хранения</div>
            <input
              className="field-input"
              value={draft.storage_location}
              placeholder="коробка 2, верхняя полка"
              onChange={(e) => set('storage_location', e.target.value)}
            />

            <div className="label-key">Заметки</div>
            <textarea
              className="field-input field-area"
              rows={4}
              value={draft.notes}
              onChange={(e) => set('notes', e.target.value)}
            />
          </>
        )}

        {error && (
          <div className="notice notice--danger" style={{ marginTop: 'var(--space-4)' }}>
            {error}
          </div>
        )}

        <button
          className="btn btn--primary btn--full"
          style={{ marginTop: 'var(--space-5)' }}
          disabled={!canSave || busy}
          onClick={save}
        >
          {uploading ?? (busy ? 'Сохраняем…' : editing ? 'Сохранить' : 'Создать образец')}
        </button>
        {!canSave && <div className="hint centered">Достаточно указать главный минерал</div>}
      </div>
    </div>
  );
}
